import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Button, CircularProgress, Fab, IconButton, Tooltip } from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import SendIcon from "@mui/icons-material/Send";
import { useSnackbar } from "notistack";
import AuthContext from "../../Hooks/AuthContext/AuthContext";
import AssistantMarkdown from "./AssistantMarkdown";
import {
  AssistantApiError,
  CHAT_MAX_LENGTH,
  ERROR_MESSAGES,
  clearStoredSessionId,
  deleteSession,
  getStoredSessionId,
  sendChat,
  setStoredSessionId,
  toAssistantError,
} from "./assistantApi";

type ChatMessage = {
  id: number;
  role: "user" | "assistant" | "error";
  text: string;
  requestId?: string;
};

export const AI_NOTICE = "Answers are generated by AI. Check them against the reports.";

const AssistantWidget = () => {
  const { auth, logout, hasPermission } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  // Set when the proxy answers 403 (not allowed / not in the pilot) or 503 (kill switch).
  const [hiddenByServer, setHiddenByServer] = useState(false);

  // Bumped on "New conversation" and sign-out; a reply from an older generation is ignored.
  const generation = useRef(0);
  const nextId = useRef(1);
  const listRef = useRef<HTMLDivElement>(null);
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  const userId = auth?.user?.id ?? null;
  const signedIn = Boolean(auth?.user);
  // Panel-side switch (REACT_APP_ASSISTANT_ENABLED=true in .env). Off unless set to "true".
  const enabledByEnv = process.env.REACT_APP_ASSISTANT_ENABLED === "true";
  const eligible =
    enabledByEnv && signedIn && auth?.role !== "agent" && hasPermission("assistant", ["VIEW"]) && !hiddenByServer;

  // Keep the token: logout() clears it from localStorage before our sign-out cleanup runs.
  const tokenRef = useRef<string | null>(null);
  if (auth?.user) tokenRef.current = auth.access_token || localStorage.getItem("token");

  const resetConversation = useCallback((deleteOnServer: boolean, token?: string | null) => {
    generation.current += 1;
    const sessionId = getStoredSessionId();
    clearStoredSessionId();
    setMessages([]);
    setInput("");
    setPending(false);
    if (deleteOnServer && sessionId) {
      deleteSession(sessionId, token).catch(() => {
        /* best effort: the server expires idle sessions */
      });
    }
  }, []);

  // Sign-out (or a different user) clears the conversation and deletes the session.
  const previousUser = useRef<{ id: unknown; token: string | null }>({ id: null, token: null });
  useEffect(() => {
    const prev = previousUser.current;
    if (prev.id !== null && prev.id !== undefined && prev.id !== userId) {
      resetConversation(true, prev.token);
      setHiddenByServer(false);
      setOpen(false);
    }
    previousUser.current = { id: userId, token: tokenRef.current };
  }, [userId, resetConversation]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, pending, open]);

  const addMessage = (m: Omit<ChatMessage, "id">) => setMessages((prev) => [...prev, { ...m, id: nextId.current++ }]);

  const handleError = (err: AssistantApiError, addToList: boolean) => {
    switch (err.kind) {
      case "disabled":
      case "forbidden":
        setHiddenByServer(true);
        setOpen(false);
        return;
      case "expired":
        if (addToList) addMessage({ role: "error", text: ERROR_MESSAGES.expired });
        enqueueSnackbar(ERROR_MESSAGES.expired, { variant: "warning" });
        // No point deleting the session with a token the server just refused.
        clearStoredSessionId();
        logoutRef.current();
        return;
      default:
        if (addToList) addMessage({ role: "error", text: ERROR_MESSAGES[err.kind] });
    }
  };

  const trimmed = input.trim();
  const tooLong = trimmed.length > CHAT_MAX_LENGTH;
  const canSend = !pending && trimmed.length > 0 && !tooLong;

  const send = async () => {
    if (!canSend) return;
    const text = trimmed;
    const gen = generation.current;
    addMessage({ role: "user", text });
    setInput("");
    setPending(true);
    try {
      const result = await sendChat(text, getStoredSessionId());
      if (gen !== generation.current) return; // new conversation or sign-out while waiting
      setStoredSessionId(result.session_id);
      addMessage({ role: "assistant", text: result.reply, requestId: result.request_id });
    } catch (err) {
      if (gen !== generation.current) return;
      handleError(toAssistantError(err), true);
    } finally {
      if (gen === generation.current) setPending(false);
    }
  };

  const newConversation = () => resetConversation(true, tokenRef.current);

  if (!eligible) return null;

  return (
    <>
      {!open && (
        <Tooltip title="Ask the assistant" placement="left">
          <Fab
            color="primary"
            size="medium"
            aria-label="Open assistant"
            onClick={() => setOpen(true)}
            sx={{ position: "fixed", right: 16, bottom: 16, zIndex: 1200 }}
          >
            <ChatBubbleOutlineIcon />
          </Fab>
        </Tooltip>
      )}

      {/* Stays mounted while closed so the conversation survives opening and closing. */}
      <section
        role="complementary"
        aria-label="AAA Assistant"
        hidden={!open}
        className="fixed bottom-0 right-0 top-16 z-[1200] flex w-full flex-col border-l border-slate-200 bg-white shadow-xl sm:w-[400px]"
        style={{ display: open ? "flex" : "none" }}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
        }}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
          <h2 className="text-sm font-semibold text-slate-800">AAA Assistant</h2>
          <div className="flex items-center">
            <Button
              size="small"
              startIcon={<AddCommentOutlinedIcon fontSize="small" />}
              onClick={newConversation}
              disabled={messages.length === 0 && !pending}
            >
              New conversation
            </Button>
            <IconButton size="small" aria-label="Close assistant" onClick={() => setOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </header>

        <div ref={listRef} role="log" aria-live="polite" className="flex-1 space-y-3 overflow-y-auto bg-slate-50 px-3 py-3">
          {messages.length === 0 && (
            <p className="text-sm text-slate-500">
              Ask a question about your reports, for example &quot;How many processes are at the Medical step?&quot;
            </p>
          )}
          {messages.map((m) =>
            m.role === "user" ? (
              <div key={m.id} className="flex justify-end">
                <div
                  data-testid="user-message"
                  className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-sm bg-violet-600 px-3 py-2 text-sm text-white"
                >
                  {m.text}
                </div>
              </div>
            ) : m.role === "error" ? (
              <div key={m.id} role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {m.text}
              </div>
            ) : (
              <div key={m.id} className="flex justify-start">
                <div className="max-w-[92%]">
                  <div data-testid="assistant-message" className="rounded-2xl rounded-bl-sm border border-slate-200 bg-white px-3 py-2 text-slate-800">
                    <AssistantMarkdown text={m.text} />
                  </div>
                </div>
              </div>
            )
          )}
          {pending && (
            <div className="flex items-center gap-2 text-sm text-slate-500" role="status">
              <CircularProgress size={16} /> Thinking...
            </div>
          )}
        </div>

        <footer className="border-t border-slate-200 bg-white p-3">
          <div className="flex items-end gap-2">
            <textarea
              aria-label="Message"
              className="max-h-32 min-h-[40px] flex-1 resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
              rows={2}
              placeholder="Ask a question"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
            />
            <Button variant="contained" aria-label="Send" disabled={!canSend} onClick={send} sx={{ minWidth: 0, px: 1.5, height: 40 }}>
              <SendIcon fontSize="small" />
            </Button>
          </div>
          <div className="mt-1 flex items-center justify-between gap-2 text-xs">
            <span className="text-slate-500">{AI_NOTICE}</span>
            <span data-testid="char-count" className={tooLong ? "shrink-0 font-semibold text-red-600" : "shrink-0 text-slate-400"}>
              {input.length.toLocaleString("en-US")} / {CHAT_MAX_LENGTH.toLocaleString("en-US")}
            </span>
          </div>
          {tooLong && (
            <p role="alert" className="mt-1 text-xs text-red-600">
              Messages can be at most {CHAT_MAX_LENGTH.toLocaleString("en-US")} characters.
            </p>
          )}
        </footer>
      </section>
    </>
  );
};

export default AssistantWidget;
